import { Box, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HEADER_HEIGHT } from "./general/header/Header";
import DefaultButton from "./general/buttons/DefaultButton";
import { resetPassword } from "../services/user";
import { useAuth } from "../services/auth";
import { colors } from "../theme";

/**
 * Where a password-reset email lands. Public, like the invite page - someone resetting
 * their password by definition can't be signed in.
 *
 * A successful reset also signs them in (the response carries the session cookie), so
 * there's no reason to send them back through the login form.
 */
export default function ResetPasswordPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { onAuthenticated } = useAuth();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

    const handleSubmit = async () => {
        if (!token) return;
        if (!password || !confirmPassword) {
            setError("Enter your new password twice.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        setError(null);
        setSaving(true);
        try {
            await resetPassword(token, password);
            onAuthenticated();
            navigate("/dashboard");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't reset your password");
            setSaving(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: colors.sidebar }}>
            <Header />
            <Box
                sx={{
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        p: 4,
                        borderRadius: 3,
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                    }}
                >
                    <Typography variant="h5" sx={{ mb: 0.5 }}>Choose a new password</Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        At least 8 characters.
                    </Typography>

                    <TextField
                        label="New password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        label="Confirm new password"
                        type="password"
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={mismatch}
                        helperText={mismatch ? "Passwords do not match." : " "}
                        sx={{ mb: 1 }}
                    />

                    {error && (
                        <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                            {error}
                        </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <DefaultButton
                            text={saving ? "Saving…" : "Set new password"}
                            onClick={handleSubmit}
                            disabled={saving || mismatch}
                            fullWidth
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
